/**
 * useSprints — the React binding for a room's sprints, scoped to the active
 * room (exactly like {@link useTasks}).
 *
 * Strict layering: components call THIS hook; the hook calls {@link SprintStore};
 * the store calls the api facade. Components never import the store or the api
 * directly.
 *
 * Phase 2 uses this only to resolve the room's CURRENT sprint for the
 * quick-create "Current Sprint | Backlog" choice; the full sprint-planning
 * surface arrives in a later phase and can build on the same hook.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { useActiveRoom } from '../rooms/RoomContext';
import { SprintStore } from './stores/sprintStore';
import { sortSprintsByRecency } from './models/sprintboardModel';
import type { Sprint, SprintDraft } from './models/types';

export interface UseSprintsResult {
  /** The room these sprints are scoped to, or null when none is active. */
  roomId: string | null;
  /** The active room's sprints (recency-sorted, newest first). */
  sprints: Sprint[];
  /**
   * The room's "current" sprint: the active one if present, else the most
   * recent by start date. Null when the room has no sprints.
   */
  currentSprint: Sprint | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Creates a sprint in the active room (status defaults to 'planning'). */
  create: (draft: SprintDraft) => Promise<Sprint>;
}

export function useSprints(): UseSprintsResult {
  const api = useApi();
  const store = useMemo(() => new SprintStore(api), [api]);
  const { activeRoomId } = useActiveRoom();

  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!activeRoomId) {
      setSprints([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const rows = await store.listByRoom(activeRoomId);
      setSprints(sortSprintsByRecency(rows));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sprints.');
    } finally {
      setLoading(false);
    }
  }, [store, activeRoomId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const currentSprint = useMemo<Sprint | null>(() => {
    if (sprints.length === 0) return null;
    return sprints.find((s) => s.status === 'active') ?? sprints[0];
  }, [sprints]);

  const create = useCallback(
    async (draft: SprintDraft) => {
      if (!activeRoomId) {
        throw new Error('Select a room before creating a sprint.');
      }
      const saved = await store.create(activeRoomId, draft);
      await refresh();
      return saved;
    },
    [store, activeRoomId, refresh],
  );

  return {
    roomId: activeRoomId,
    sprints,
    currentSprint,
    loading,
    error,
    refresh,
    create,
  };
}
